import * as Yup from "yup";
import { URL_REGEX, validationResult } from "./patterns";
import { runFlatFormSchema } from "./yupFormUtils";

const VALID_KINDS = ["splunk_hec", "elastic_bulk", "sentinel_logs", "datadog", "webhook"];

function validSecretRef(s) {
  return /^arn:aws:secretsmanager:/.test(s) || /^env:[A-Z_][A-Z0-9_]*$/.test(s);
}

function validateEndpoint(value, kind) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return validationResult(false, "Endpoint URL is required");
  if (!URL_REGEX.test(trimmed)) {
    return validationResult(false, "Enter a valid URL");
  }
  if (kind !== "webhook" && !/^https:\/\//i.test(trimmed)) {
    return validationResult(false, `${kind} endpoint must be HTTPS`);
  }
  return validationResult(true);
}

export function validateSiemDestinationForm(form = {}) {
  const kind = form.kind || "webhook";
  const enabled = form.enabled !== false;
  const secretRef = String(form.secret_ref ?? "").trim();

  const schema = Yup.object({
    name: Yup.string()
      .trim()
      .required("Name is required")
      .min(1, "Name must be 1–64 characters")
      .max(64, "Name must be 1–64 characters"),
    kind: Yup.string().oneOf(VALID_KINDS, "Select a valid kind").required("Kind is required"),
    endpoint: Yup.string().test("endpoint", function (value) {
      const result = validateEndpoint(value, kind);
      return result.valid || this.createError({ message: result.message });
    }),
    secret: Yup.string().test("secret", function (value) {
      const trimmed = String(value ?? "").trim();
      if (enabled && !trimmed && !secretRef) {
        return this.createError({
          message: "Provide a secret or secret reference for enabled destinations",
        });
      }
      if (trimmed && trimmed.length > 1024) {
        return this.createError({ message: "Secret must be 1–1,024 characters" });
      }
      return true;
    }),
    secret_ref: Yup.string().test("secret_ref", function (value) {
      const ref = String(value ?? "").trim();
      if (!ref) return true;
      if (!validSecretRef(ref)) {
        return this.createError({
          message: "Must be arn:aws:secretsmanager:... or env:VARNAME",
        });
      }
      return true;
    }),
    datasets: Yup.array().min(1, "Select at least one dataset"),
    rate_limit_eps: Yup.number()
      .typeError("Rate must be a number")
      .integer("Rate must be a whole number")
      .min(1, "Rate must be between 1 and 100,000")
      .max(100000, "Rate must be between 1 and 100,000")
      .required("Rate is required"),
    batch_max: Yup.number()
      .typeError("Batch max must be a number")
      .integer("Batch max must be a whole number")
      .min(1, "Batch max must be between 1 and 10,000")
      .max(10000, "Batch max must be between 1 and 10,000")
      .required("Batch max is required"),
    flush_seconds: Yup.number()
      .typeError("Flush must be a number")
      .integer("Flush must be a whole number")
      .min(1, "Flush must be between 1 and 60 seconds")
      .max(60, "Flush must be between 1 and 60 seconds")
      .required("Flush is required"),
    notes: Yup.string().max(500, "Notes must be 500 characters or fewer"),
  });

  return runFlatFormSchema(schema, {
    name: form.name,
    kind: form.kind,
    endpoint: form.endpoint,
    secret: form.secret,
    secret_ref: form.secret_ref,
    datasets: form.datasets,
    rate_limit_eps: form.rate_limit_eps,
    batch_max: form.batch_max,
    flush_seconds: form.flush_seconds,
    notes: form.notes,
    enabled,
  });
}
